/**
 * Builds and cleans the request body for account create/update submission.
 * Removes role-specific fields and omits address when only type is set.
 * @param {Object} userData - Registration/account form state
 * @param {Array} patientLocations - Patient location records (for Patient role)
 * @returns {Object} Cleaned submission payload
 */
export const cleanupRequestBody = (userData, patientLocations = []) => {
    const { organizationId: _omitRoleOrg, ...roleWithoutOrgId } = userData.role ?? {};
    const submissionData = {
        firstName: userData.firstName,
        middleName: userData.middleName,
        lastName: userData.lastName,
        email: userData.email,
        countryCode: userData.countryCode,
        phone: userData.phone,
        ehrAccount: userData.ehrAccount,
        role: roleWithoutOrgId,
        groups: userData.groups,
        mrn: userData.mrn,
        ssn: userData.ssn,
        npi: userData.npi,
        canHaveAppointment: userData.canHaveAppointment,
        canManageAvailability: userData.canManageAvailability,
        emergencyContact: userData.emergencyContact,
        healthInfo: userData.healthInfo,
        specialties: userData.specialties,
        title: userData.title,
        employeeId: userData.employeeId,
        preferences: userData.preferences,
        address: userData.address
    };
    const updating = userData.updating;

    if (updating) {
        delete submissionData.ehrAccount;
        delete submissionData.ssn;
    }

    if (userData.role.category === "Admin" || userData.role.category === "Staff") {
        delete submissionData.emergencyContact;
        delete submissionData.healthInfo;
        delete submissionData.specialties;
        delete submissionData.ssn;
    } else if (userData.role.category === "Doctor") {
        delete submissionData.emergencyContact;
        delete submissionData.healthInfo;
        delete submissionData.ssn;
    } else if (userData.role.category === "Patient") {
        delete submissionData.employeeId;
        delete submissionData.npi;
        delete submissionData.specialties;
        delete submissionData.title;
        if (userData.patient_id != null && userData.ehrAccount && !updating) {
            const patientLocation = patientLocations.find(
                (location) => location.mrn === userData.mrn
            );
            const inpatientTeams = userData.groups.map((group) => ({
                ...group,
                floor: patientLocation?.floor ?? null,
                room: patientLocation?.room ?? null,
                bed: patientLocation?.bed ?? null,
                location: patientLocation?.location ?? null,
                encounterId: patientLocation?.encounterId ?? null,
                mirthId: patientLocation?.patient_id ?? null,
            }));
            submissionData.ehrTeams = inpatientTeams;
        }
    }

    // Omit address from request if only type is set (all other fields empty)
    if (userData.address) {
        const { type, ...rest } = userData.address;
        const addressFieldsEmpty = Object.values(rest).every(
            (val) => val === "" || val == null
        );
        if (addressFieldsEmpty) {
            delete submissionData.address;
        }
    }
    return submissionData;
};

/**
 * Normalizes a height input into metric centimeters.
 *
 * Shared by:
 * - ActivateHealthInfo
 * - HealthInfo (registration)
 * - HealthInfoDetail (patient detail)
 *
 * @param {boolean} isMetric - Whether the UI is in metric mode
 * @param {{cm?: string|number, feet?: string|number, inches?: string|number}} height
 * @returns {number|string|null} - cm value (number), empty string to clear, or null to leave unchanged
 */
export const normalizeHeightToCm = (isMetric, height) => {
    if (!height) {
        return null;
    }

    if (isMetric) {
        const cm = height.cm;
        const cmStr = cm != null ? String(cm) : "";
        if (/^\d+$/.test(cmStr)) {
            return Number(cm);
        }
        return null;
    }

    const feet = height.feet;
    const inches = height.inches;
    const feetStr = feet != null ? String(feet) : "";
    const inchesStr = inches != null ? String(inches) : "";

    if (
        feetStr === "" ||
        inchesStr === "" ||
        /^\d+$/.test(feetStr) ||
        /^\d+$/.test(inchesStr)
    ) {
        const numericFeet = feetStr === "" ? 0 : Number(feetStr);
        const numericInches = inchesStr === "" ? 0 : Number(inchesStr);

        if (Number.isNaN(numericFeet) || Number.isNaN(numericInches)) {
            return null;
        }

        if (numericFeet === 0 && numericInches === 0) {
            return "";
        }

        const totalInches = numericFeet * 12 + numericInches;
        const metricVal = Number((totalInches * 2.54).toFixed(2));
        return metricVal;
    }

    return null;
};

/**
 * Builds the local height state object from a stored metric height value.
 *
 * @param {boolean} isMetric
 * @param {string|number} metricHeight - Stored height in centimeters
 * @returns {{cm: number}|{feet: number, inches: number}|{feet: string, inches: string, cm: string}}
 */
export const buildHeightState = (isMetric, metricHeight) => {
    const val = parseFloat(metricHeight);
    if (isNaN(val)) {
        return { feet: "", inches: "", cm: "" };
    }

    if (isMetric) {
        return { cm: val };
    }

    const totalInches = val / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
};

/**
 * Builds the local weight state (display units) from a stored metric weight (kg).
 *
 * @param {boolean} isMetric
 * @param {string|number} metricWeight - Stored weight in kilograms
 * @returns {number|string} - Display weight (kg or rounded lb), or "" if invalid
 */
export const buildWeightState = (isMetric, metricWeight) => {
    const val = parseFloat(metricWeight);
    if (isNaN(val)) {
        return "";
    }

    if (!isMetric) {
        return Math.round(val * 2.20462262185);
    }

    return val;
};

/**
 * Normalizes weight input to metric kg. Same rules as HealthInfo / HealthInfoDetail blur.
 *
 * @param {boolean} isMetric
 * @param {string|number} weight - Display weight (kg or lb)
 * @returns {number|string|null} - kg value, "" to clear, or null if invalid/unchanged
 */
export const normalizeWeightToKg = (isMetric, weight) => {
    if (weight === "" || weight === null || weight === undefined) {
        return "";
    }
    const str = String(weight).trim();
    if (str === "") {
        return "";
    }
    const numericValue = Number(str);
    if (Number.isNaN(numericValue)) {
        return null;
    }
    if (numericValue === 0) {
        return "";
    }
    let metricValue = numericValue;
    if (!isMetric) {
        metricValue = Number((numericValue * 0.45359237).toFixed(2));
    }
    return metricValue;
};

export const parseInitials = (fullName) => {
    return fullName?.split(' ').map(word => word[0]).join('').toUpperCase() ?? "";
}